import AsyncGroupFactory from '@/Factories/AsyncGroupFactory';
import ComponentManager from '@/Management/ComponentManager';
import WizardGroupBoxViewModelT from '@/ViewModels/Controls/WizardGroupBoxViewModel';
import WizardGroupBoxT from '@/Controls/WizardGroupBox';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import TabStripViewModelT from '@/ViewModels/Controls/TabStripViewModel';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Messenger from '@/Utility/Messenger';
import NavigationService from '@/Management/NavigationService';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import { IWizardGroupFactoryConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import TabStripT from '@/Controls/TabStrip';

export default class WizardGroupFactory extends AsyncGroupFactory {

    protected WizardGroupBoxViewModel = ComponentManager.getViewModel<typeof WizardGroupBoxViewModelT>('WizardGroupBoxViewModel');
    protected WizardGroupBox = ComponentManager.getControl<typeof WizardGroupBoxT>('WizardGroupBox');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');

    /**
     * @override 
     */
    public viewModel: WizardGroupBoxViewModelT;

    /**
     * Der innenliegende TabStrip
     */
    public tabStripViewModel: TabStripViewModelT;

    /**
     * Der innenliegende TabStrip
     */
    public parentTabStrip: TabStripT;

    /**
     * Gibt an, ob die Buttons zur Navigation in der Toolbar angezeigt werden sollen
     */
    public showBrowseButtonsInToolbar: KnockoutObservable<boolean>;

    /**
     * Gibt den aktuell selektierten Index zurück
     */
    public selectedIndex: KnockoutObservable<number>;

    /**
     * Gibt den Namen des aktuellen Wizards zurück
     */
    public selectedWizardName: KnockoutComputed<string>;

    public isBusy: KnockoutObservable<boolean>;

    public constructor(params: IWizardGroupFactoryConstructorParameter) {
        super(params);
        this.colspan = this.paramOrDefault(params.colspan, 'Fill');
        this.layout = this.paramOrDefault(params.layout, 'OneColumnLayout');
        this.tabStripViewModel = this.paramOrDefault(params.tabStrip, null);
        this.parentTabStrip = this.paramOrDefault(params.parentTabStrip, null);
        this.showBrowseButtonsInToolbar = this.observableOrDefault(params.showBrowseButtonsInToolbar, false);
        this.selectedIndex = this.observableOrDefault(params.selectedIndex, 0);
        // TODO: Gehört ins ViewModel
        this.selectedWizardName = this.defineComputed(() => {
            var index = this.selectedIndex();
            if (!isNaN(index)) {
                if (index === 0) {
                    return this.detailViewGroup.name;
                }
                else if (index > 0 && this.viewModel) {
                    const dto = this.viewModel.detailViewGroups[index - 1];
                    if (dto) {
                        return dto.name;
                    }
                }
            }
            return null;
        });
        this.isBusy = this.observableOrDefault(params.isBusy, false);
    }

    protected manageWizardPageIndex() {
        this.trackWizardPageIndex();
        this.getSelectedIndexFromNavStack();
    }

    private trackWizardPageIndex(): void {
        this.defineSubscription(this.selectedIndex, (selectedIndex) => {
            if (!NavigationService.getInstance().getLastHistoryStateStackItem())
                return;

            const currentPdoId = (this.context as DetailViewContext).genericObject?.pdoId;
            if (!currentPdoId)
                return;

            let wizardIndexes = NavigationService.getInstance().getInformationFromCurrentState('wizardIndexes');
            const isWizardIndexesExist = !!wizardIndexes;

            // Wenn "wizardIndexes" noch nicht existiert, wird es neu erstellt.
            wizardIndexes = wizardIndexes || {};

            // Wenn keine Map für das aktuelle PdoID vorhanden ist, eine neue erstellen
            wizardIndexes[currentPdoId] = wizardIndexes[currentPdoId] || new Map();

            // Den aktuellen Index für die Wizard-Gruppe unter dem entsprechenden PdoID speichern
            wizardIndexes[currentPdoId].set(this.detailViewGroup.name, selectedIndex);

            if (!isWizardIndexesExist) {
                NavigationService.getInstance().addInformationToCurrentState({ wizardIndexes });
            }
        })
    }

    private getSelectedIndexFromNavStack(): void {
        const wizardIndexes = NavigationService.getInstance().getInformationFromCurrentState('wizardIndexes');
        if (!wizardIndexes)
            return;

        const currentPdoId = (this.context as DetailViewContext).genericObject?.pdoId;

        if (wizardIndexes[currentPdoId]) {

            const index = wizardIndexes[currentPdoId].get(this.detailViewGroup.name)

            if (index !== undefined)
                this.selectedIndex(index);
        }
    }

    /**
     * @override 
     */
    public async getElementAsync(): Promise<JQuery<HTMLElement>> {
        const $element = await super.getElementAsync();

        if (!this.parentTabStrip) {
            Messenger.createMessageAndSend('WizardCreatedMessage', {
                wizard: this
            });

            this.manageWizardPageIndex();
        }

        return $element;
    }

    /**
     * @override 
     */
    protected createViewModel(): WizardGroupBoxViewModelT {
        return new this.WizardGroupBoxViewModel({
            context: this.context,
            isVisible: this.isVisible,
            layout: this.layout,
            selectedIndex: this.selectedIndex,
            showBrowseButtonsInToolbar: this.showBrowseButtonsInToolbar,
            tabStrip: this.tabStripViewModel,
            parentTabStrip: this.parentTabStrip,
            isSummaryViewMode: this.isSummaryViewMode,
            detailViewGroup: this.detailViewGroup,
            detailViewGroups: this.getInnerWizardGroups(this.detailViewGroup),
            locator: this.locator, // In diesem speziellen Fall werden die eigentlichen Wizard-Schritte als Untergruppen modelliert; daher darf der Locator hier noch nicht angepasst werden
            isBusy: this.isBusy,
        });
    }

    /**
     * @override 
     */
    protected createControl(): WizardGroupBoxT {
        return new this.WizardGroupBox(this.viewModel);
    }

    /**
     * Liefert die inneren WizardGroups
     * @param detailViewGroup
     */
    private getInnerWizardGroups(detailViewGroup: CoreFrameDTOs.IDetailViewGroupDTO): CoreFrameDTOs.IDetailViewGroupDTO[] {
        const result: CoreFrameDTOs.IDetailViewGroupDTO[] = [];

        if (detailViewGroup.detailViewGroups != null) {
            for (let i = 0; i < detailViewGroup.detailViewGroups.length; i++) {
                const innerGroup = detailViewGroup.detailViewGroups[i];

                if (innerGroup.uiGenerator.indexOf('WizardGroup') === 0) {
                    result.push(innerGroup);
                }
            }
        }

        return result;
    }
}

ComponentManager.setFactory('WizardGroup', WizardGroupFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');